import type { ProductModuleRow } from "@/api/admin.api";

/**
 * Switches in this group aren't menu items: they turn selling on or off (e.g. add-on packs). They
 * get their own card and stay out of the presets and the sidebar preview.
 */
export const SELLING_GROUP = "Selling";

export const splitSelling = (rows: ProductModuleRow[]) => ({
  menu: rows.filter((row) => row.groupName !== SELLING_GROUP),
  selling: rows.filter((row) => row.groupName === SELLING_GROUP),
});

/** The simplified product: only these show. */
export const LAUNCH_PRESET_KEYS = ["resumes", "practice_interview"];

/** Unsaved switch changes, by key. A key is present only while it differs from the saved state. */
export type Draft = Record<string, boolean>;

export const isOn = (row: ProductModuleRow, draft: Draft) => draft[row.key] ?? row.enabled;

/** Flip one switch; flipping it back to its saved state drops it from the draft. */
export const toggle = (draft: Draft, row: ProductModuleRow, enabled: boolean): Draft => {
  const next = { ...draft };
  if (enabled === row.enabled) delete next[row.key];
  else next[row.key] = enabled;
  return next;
};

/** A draft that leaves exactly `keys` on (all rows when `keys` is "all"). */
export const preset = (rows: ProductModuleRow[], keys: string[] | "all"): Draft =>
  rows.reduce<Draft>((draft, row) => {
    const enabled = keys === "all" || keys.includes(row.key);
    return toggle(draft, row, enabled);
  }, {});

export const changes = (rows: ProductModuleRow[], draft: Draft) => ({
  showing: rows.filter((row) => draft[row.key] === true),
  hiding: rows.filter((row) => draft[row.key] === false),
});

export type PreviewEntry =
  | { kind: "item"; name: string; changed: boolean }
  | { kind: "group"; name: string; items: { name: string; changed: boolean }[] };

/**
 * theResume's sidebar for these switches, after Dashboard. Same rule as theResume's sidebar
 * (sidebar-nav.ts there): a group with nothing on disappears, a group with one item on shows that
 * item on its own. Rows come in display order; grouped items sit together.
 */
export const previewSidebar = (rows: ProductModuleRow[], draft: Draft): PreviewEntry[] => {
  const sections: { group: string | null; rows: ProductModuleRow[] }[] = [];
  for (const row of [...rows].sort((a, b) => a.displayOrder - b.displayOrder)) {
    const last = sections[sections.length - 1];
    if (row.groupName && last?.group === row.groupName) last.rows.push(row);
    else sections.push({ group: row.groupName, rows: [row] });
  }

  return sections.flatMap((section): PreviewEntry[] => {
    const items = section.rows
      .filter((row) => isOn(row, draft))
      .map((row) => ({ name: row.name, changed: row.key in draft }));
    if (items.length === 0) return [];
    if (!section.group || items.length === 1) {
      return items.map((item) => ({ kind: "item", ...item }));
    }
    return [{ kind: "group", name: section.group, items }];
  });
};
